import React, { useState, useMemo } from 'react';
import { Conta, Banco, TipoContaId } from '../../types/financeiro';
import { Pessoa } from '../../types/pessoa';
import { EmpresaConfig } from '../../types/empresa';
import { sanitizeText } from '../../utils/textSanitizer';
import { MaskedValue } from '../common/ProtectedField';
import { 
  FileSpreadsheet, 
  Printer, 
  Filter, 
  Building2, 
  Landmark, 
  Calendar, 
  DollarSign, 
  TrendingUp, 
  TrendingDown, 
  Scale, 
  Search, 
  RotateCcw,
  CheckCircle2,
  Clock,
  Receipt,
  Download,
  PieChart
} from 'lucide-react';

interface ExtratoCnpjBancoSectionProps {
  contas: Conta[];
  bancos: Banco[];
  empresas: EmpresaConfig[];
  pessoasMap: Map<number, string>;
  loading?: boolean;
}

export function ExtratoCnpjBancoSection({
  contas,
  bancos,
  empresas,
  pessoasMap,
  loading = false
}: ExtratoCnpjBancoSectionProps) {
  // Filtros
  const [selectedCnpj, setSelectedCnpj] = useState<string>('all');
  const [selectedBanco, setSelectedBanco] = useState<string>('all');
  const [selectedTipo, setSelectedTipo] = useState<'all' | 'pagar' | 'receber'>('all');
  const [selectedStatus, setSelectedStatus] = useState<'all' | 'quitada' | 'aberta'>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [periodPreset, setPeriodPreset] = useState<string>('mes_atual');
  
  // Custom Date Range
  const todayStr = new Date().toISOString().split('T')[0];
  const firstDayThisMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0];
  const lastDayThisMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0];

  const [startDate, setStartDate] = useState<string>(firstDayThisMonth);
  const [endDate, setEndDate] = useState<string>(lastDayThisMonth);

  // Mudar presets de período
  const handlePeriodChange = (preset: string) => {
    setPeriodPreset(preset);
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();

    if (preset === 'mes_atual') {
      setStartDate(new Date(y, m, 1).toISOString().split('T')[0]);
      setEndDate(new Date(y, m + 1, 0).toISOString().split('T')[0]);
    } else if (preset === 'mes_anterior') {
      setStartDate(new Date(y, m - 1, 1).toISOString().split('T')[0]);
      setEndDate(new Date(y, m, 0).toISOString().split('T')[0]);
    } else if (preset === 'ultimos_3_meses') {
      setStartDate(new Date(y, m - 2, 1).toISOString().split('T')[0]);
      setEndDate(new Date(y, m + 1, 0).toISOString().split('T')[0]);
    } else if (preset === 'ano_atual') {
      setStartDate(`${y}-01-01`);
      setEndDate(`${y}-12-31`);
    } else if (preset === 'todos') {
      setStartDate('');
      setEndDate('');
    }
  };

  // Identificar lista única de CNPJs faturadores presentes nos dados
  const cnpjsPresentes = useMemo(() => {
    const set = new Set<string>();
    
    // Adicionar as empresas cadastradas
    empresas.forEach(emp => {
      const label = emp.cnpj && emp.cnpj.length > 5 
        ? `${emp.cnpj} - ${emp.nome_fantasia || emp.razao_social}` 
        : emp.nome_fantasia || emp.razao_social;
      if (label) set.add(label);
    });
    
    contas.forEach(c => {
      if (c.cnpj_faturador && c.cnpj_faturador.trim()) {
        const val = c.cnpj_faturador.trim();
        let matched = false;
        empresas.forEach(emp => {
          if ((emp.cnpj && val.includes(emp.cnpj)) || (emp.nome_fantasia && val.toLowerCase().includes(emp.nome_fantasia.toLowerCase()))) {
            const label = emp.cnpj && emp.cnpj.length > 5 
              ? `${emp.cnpj} - ${emp.nome_fantasia || emp.razao_social}` 
              : emp.nome_fantasia || emp.razao_social;
            if (label) {
              set.add(label);
              matched = true;
            }
          }
        });
        if (!matched) {
          set.add(val);
        }
      }
    });
    return Array.from(set);
  }, [contas, empresas]);

  // Função auxiliar para normalizar CNPJ de uma conta
  const getNormalizedCnpj = (conta: Conta): string => {
    if (!conta.cnpj_faturador) return 'Não Informado / Geral';
    const c = conta.cnpj_faturador.trim();
    
    for (const emp of empresas) {
      if ((emp.cnpj && c.includes(emp.cnpj)) || (emp.nome_fantasia && c.toLowerCase().includes(emp.nome_fantasia.toLowerCase()))) {
        return emp.cnpj && emp.cnpj.length > 5 
          ? `${emp.cnpj} - ${emp.nome_fantasia || emp.razao_social}` 
          : emp.nome_fantasia || emp.razao_social || c;
      }
    }
    
    return c;
  };

  // Função auxiliar para normalizar Banco de uma conta
  const getNormalizedBanco = (conta: Conta): string => {
    if (conta.banco_nome && conta.banco_nome.trim()) return conta.banco_nome.trim();
    if (conta.banco_id) {
      const b = bancos.find(x => Number(x.id) === Number(conta.banco_id));
      if (b?.nome) return b.nome.trim();
    }
    return 'Não Especificado / Outros';
  };

  // Filtragem dos dados para o extrato
  const filteredContas = useMemo(() => {
    return contas.filter(c => {
      // Filtro de Tipo
      if (selectedTipo === 'pagar' && Number(c.tipo_conta_id) !== TipoContaId.PAGAR) return false;
      if (selectedTipo === 'receber' && Number(c.tipo_conta_id) !== TipoContaId.RECEBER) return false;

      // Filtro de Status
      const isQuitada = Boolean(c.dt_pagamento);
      if (selectedStatus === 'quitada' && !isQuitada) return false;
      if (selectedStatus === 'aberta' && isQuitada) return false;

      // Filtro de Data (usa dt_pagamento se quitada ou dt_vencimento se aberta)
      const dataRef = c.dt_pagamento || c.dt_vencimento || c.dt_emissao || '';
      if (startDate && dataRef && dataRef < startDate) return false;
      if (endDate && dataRef && dataRef > endDate) return false;

      // Filtro de CNPJ
      if (selectedCnpj !== 'all') {
        const normCnpj = getNormalizedCnpj(c);
        if (normCnpj !== selectedCnpj) {
          return false;
        }
      }

      // Filtro de Banco
      if (selectedBanco !== 'all') {
        const normBanco = getNormalizedBanco(c).toLowerCase();
        if (!normBanco.includes(selectedBanco.toLowerCase())) return false;
      }

      // Busca textual
      if (searchTerm.trim()) {
        const t = searchTerm.toLowerCase();
        const desc = (c.descricao || '').toLowerCase();
        const nf = (c.numero_nf || '').toLowerCase();
        const id = c.id.toString();
        const pessoa = (pessoasMap.get(c.pessoa_id) || '').toLowerCase();
        const bnc = getNormalizedBanco(c).toLowerCase();
        const cnpj = getNormalizedCnpj(c).toLowerCase();

        const match = desc.includes(t) || nf.includes(t) || id.includes(t) || pessoa.includes(t) || bnc.includes(t) || cnpj.includes(t);
        if (!match) return false;
      }

      return true;
    }).sort((a, b) => {
      // Ordena por data decrescente
      const dateA = a.dt_pagamento || a.dt_vencimento || '';
      const dateB = b.dt_pagamento || b.dt_vencimento || '';
      return dateB.localeCompare(dateA);
    });
  }, [contas, selectedTipo, selectedStatus, startDate, endDate, selectedCnpj, selectedBanco, searchTerm, pessoasMap, bancos]);

  // Estatísticas e Totais Gerais
  const totals = useMemo(() => {
    let totalFaturadoReceber = 0;
    let totalRecebido = 0;
    let totalFaturadoPagar = 0;
    let totalPago = 0;

    filteredContas.forEach(c => {
      const isReceber = Number(c.tipo_conta_id) === TipoContaId.RECEBER;
      const isPagar = Number(c.tipo_conta_id) === TipoContaId.PAGAR;
      const nominal = Number(c.valor) || 0;
      const pago = Number(c.valor_pago || (c.dt_pagamento ? c.valor : 0)) || 0;

      if (isReceber) {
        totalFaturadoReceber += nominal;
        if (c.dt_pagamento) totalRecebido += pago;
      } else if (isPagar) {
        totalFaturadoPagar += nominal;
        if (c.dt_pagamento) totalPago += pago;
      }
    });

    const saldoNominal = totalFaturadoReceber - totalFaturadoPagar;
    const saldoEfetivo = totalRecebido - totalPago;

    return {
      totalFaturadoReceber,
      totalRecebido,
      totalFaturadoPagar,
      totalPago,
      saldoNominal,
      saldoEfetivo,
      count: filteredContas.length
    };
  }, [filteredContas]);

  // Agrupamento por CNPJ
  const resumoPorCnpj = useMemo(() => {
    const mapa = new Map<string, { faturadoReceber: number; recebido: number; faturadoPagar: number; pago: number; count: number }>();

    filteredContas.forEach(c => {
      const cnpj = getNormalizedCnpj(c);
      const current = mapa.get(cnpj) || { faturadoReceber: 0, recebido: 0, faturadoPagar: 0, pago: 0, count: 0 };
      
      const isReceber = Number(c.tipo_conta_id) === TipoContaId.RECEBER;
      const isPagar = Number(c.tipo_conta_id) === TipoContaId.PAGAR;
      const nominal = Number(c.valor) || 0;
      const pago = Number(c.valor_pago || (c.dt_pagamento ? c.valor : 0)) || 0;

      if (isReceber) {
        current.faturadoReceber += nominal;
        if (c.dt_pagamento) current.recebido += pago;
      } else if (isPagar) {
        current.faturadoPagar += nominal;
        if (c.dt_pagamento) current.pago += pago;
      }
      current.count += 1;
      mapa.set(cnpj, current);
    });

    return Array.from(mapa.entries()).map(([cnpj, data]) => ({
      cnpj,
      ...data,
      saldoEfetivo: data.recebido - data.pago,
      saldoNominal: data.faturadoReceber - data.faturadoPagar
    }));
  }, [filteredContas]);

  // Agrupamento por Banco
  const resumoPorBanco = useMemo(() => {
    const mapa = new Map<string, { totalPago: number; totalRecebido: number; count: number }>();

    filteredContas.forEach(c => {
      const banco = getNormalizedBanco(c);
      const current = mapa.get(banco) || { totalPago: 0, totalRecebido: 0, count: 0 };
      
      const isReceber = Number(c.tipo_conta_id) === TipoContaId.RECEBER;
      const isPagar = Number(c.tipo_conta_id) === TipoContaId.PAGAR;
      const pago = Number(c.valor_pago || (c.dt_pagamento ? c.valor : 0)) || 0;

      if (c.dt_pagamento) {
        if (isReceber) current.totalRecebido += pago;
        if (isPagar) current.totalPago += pago;
      }
      current.count += 1;
      mapa.set(banco, current);
    });

    return Array.from(mapa.entries()).map(([banco, data]) => ({
      banco,
      ...data,
      saldoLiquido: data.totalRecebido - data.totalPago
    })).sort((a, b) => (b.totalPago + b.totalRecebido) - (a.totalPago + a.totalRecebido));
  }, [filteredContas]);

  const formatMoney = (val: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);

  const formatDateBR = (dateStr?: string | null): string => {
    if (!dateStr) return '-';
    const parts = dateStr.split('T')[0].split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateStr;
  };

  // Exportar para CSV (compatível com Excel)
  const handleExportCSV = () => {
    if (filteredContas.length === 0) {
      alert('Não há dados filtrados para exportar.');
      return;
    }

    const headers = [
      'ID',
      'Tipo',
      'Status',
      'Data Emissao',
      'Data Vencimento',
      'Data Pagamento',
      'CNPJ / Empresa Faturadora',
      'Banco de Liquidacao',
      'Forma Pagamento',
      'Nº Nota Fiscal',
      'Favorecido / Cliente',
      'Descricao',
      'Valor Nominal (R$)',
      'Valor Pago (R$)'
    ];

    const rows = filteredContas.map(c => [
      c.id,
      Number(c.tipo_conta_id) === TipoContaId.RECEBER ? 'Receita / Receber' : 'Despesa / Pagar',
      c.dt_pagamento ? 'Quitado / Pago' : 'Pendente / Em Aberto',
      c.dt_emissao || '',
      c.dt_vencimento || '',
      c.dt_pagamento || '',
      `"${getNormalizedCnpj(c).replace(/"/g, '""')}"`,
      `"${getNormalizedBanco(c).replace(/"/g, '""')}"`,
      `"${(c.forma_pagamento_id || '').toString()}"`,
      `"${(c.numero_nf || '').replace(/"/g, '""')}"`,
      `"${(pessoasMap.get(c.pessoa_id) || '').replace(/"/g, '""')}"`,
      `"${(c.descricao || '').replace(/"/g, '""')}"`,
      (c.valor || 0).toFixed(2).replace('.', ','),
      (c.valor_pago || (c.dt_pagamento ? c.valor : 0) || 0).toFixed(2).replace('.', ',')
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,\uFEFF' + 
      [headers.join(';'), ...rows.map(e => e.join(';'))].join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `extrato_financeiro_cnpj_banco_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Impressão limpa
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="space-y-6">
      {/* Cabeçalho do Extrato & Ações */}
      <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="p-2 bg-indigo-50 text-indigo-700 rounded-xl">
              <Scale className="w-5 h-5" />
            </span>
            <div>
              <h2 className="text-lg font-black text-slate-900">Extrato Consolidado por CNPJ & Banco</h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Relatório gerencial de faturamento, liquidações e extrato detalhado por empresa e instituição bancária
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={handleExportCSV}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors cursor-pointer"
            title="Exportar dados filtrados para Excel / CSV"
          >
            <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
            <span>Exportar Excel (CSV)</span>
          </button>

          <button
            type="button"
            onClick={handlePrint}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors cursor-pointer"
            title="Imprimir extrato formatado"
          >
            <Printer className="w-4 h-4 text-slate-600" />
            <span>Imprimir Extrato</span>
          </button>
        </div>
      </div>

      {/* Cards de Métricas Consolidadas do Período */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Faturado a Receber */}
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Faturado (A Receber)</span>
            <span className="p-2 bg-emerald-50 text-emerald-700 rounded-xl">
              <TrendingUp className="w-4 h-4" />
            </span>
          </div>
          <div className="mt-2 text-xl font-extrabold text-emerald-700">
            <MaskedValue permission="view_financial_values" value={totals.totalFaturadoReceber} type="currency" />
          </div>
          <div className="mt-1 text-xs text-slate-500 flex items-center justify-between">
            <span>Recebido Efetivo:</span>
            <span className="font-bold text-emerald-800">
              <MaskedValue permission="view_financial_values" value={totals.totalRecebido} type="currency" />
            </span>
          </div>
        </div>

        {/* Total Faturado a Pagar */}
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Faturado (A Pagar)</span>
            <span className="p-2 bg-rose-50 text-rose-700 rounded-xl">
              <TrendingDown className="w-4 h-4" />
            </span>
          </div>
          <div className="mt-2 text-xl font-extrabold text-rose-700">
            <MaskedValue permission="view_financial_values" value={totals.totalFaturadoPagar} type="currency" />
          </div>
          <div className="mt-1 text-xs text-slate-500 flex items-center justify-between">
            <span>Pago Efetivo:</span>
            <span className="font-bold text-rose-800">
              <MaskedValue permission="view_financial_values" value={totals.totalPago} type="currency" />
            </span>
          </div>
        </div>

        {/* Saldo Líquido Efetivo (Pago x Recebido) */}
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Saldo Efetivo Realizado</span>
            <span className={`p-2 rounded-xl ${totals.saldoEfetivo >= 0 ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'}`}>
              <DollarSign className="w-4 h-4" />
            </span>
          </div>
          <div className={`mt-2 text-xl font-extrabold ${totals.saldoEfetivo >= 0 ? 'text-blue-700' : 'text-amber-700'}`}>
            <MaskedValue permission="view_financial_values" value={totals.saldoEfetivo} type="currency" />
          </div>
          <div className="mt-1 text-xs text-slate-500 flex items-center justify-between">
            <span>Saldo Faturado Previsto:</span>
            <span className="font-bold text-slate-700">
              <MaskedValue permission="view_financial_values" value={totals.saldoNominal} type="currency" />
            </span>
          </div>
        </div>

        {/* Total de Lançamentos Filtrados */}
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Registros no Período</span>
            <span className="p-2 bg-slate-100 text-slate-700 rounded-xl">
              <Receipt className="w-4 h-4" />
            </span>
          </div>
          <div className="mt-2 text-xl font-black text-slate-900">
            {totals.count} <span className="text-xs font-normal text-slate-500">títulos</span>
          </div>
          <div className="mt-1 text-xs text-slate-500">
            Filtrados conforme CNPJ e banco selecionados
          </div>
        </div>
      </div>

      {/* Seção de Comparativos em Destaque: Por CNPJ e Por Banco */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tabela Comparativa: Por CNPJ Faturador */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-xs p-4 space-y-3">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-indigo-600" />
              <h3 className="text-sm font-bold text-slate-900">Faturado & Pago por CNPJ</h3>
            </div>
            <span className="text-xs text-slate-500 font-medium">Comparativo entre empresas</span>
          </div>

          <div className="space-y-3">
            {resumoPorCnpj.map((item) => (
              <div key={item.cnpj} className="p-3 bg-slate-50/70 border border-slate-200 rounded-xl space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-indigo-600"></span>
                    <span className="text-xs font-bold text-slate-900">{item.cnpj}</span>
                  </div>
                  <span className="text-[11px] font-semibold text-slate-500 bg-white px-2 py-0.5 rounded border border-slate-200">
                    {item.count} títulos
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-2 pt-1 text-xs">
                  <div>
                    <span className="text-[10px] text-slate-500 block uppercase font-bold">Faturado / Previsto</span>
                    <span className="font-extrabold text-slate-800">
                      <MaskedValue permission="view_financial_values" value={item.faturadoReceber} type="currency" />
                    </span>
                  </div>
                  <div>
                    <span className="text-[10px] text-rose-500 block uppercase font-bold">Pago (Despesas)</span>
                    <span className="font-extrabold text-rose-700">
                      <MaskedValue permission="view_financial_values" value={item.pago} type="currency" />
                    </span>
                  </div>
                  <div>
                    <span className="text-[10px] text-emerald-600 block uppercase font-bold">Recebido</span>
                    <span className="font-extrabold text-emerald-700">
                      <MaskedValue permission="view_financial_values" value={item.recebido} type="currency" />
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-1 border-t border-slate-200/60 text-[11px]">
                  <span className="text-slate-500 font-medium">Resultado Líquido do CNPJ:</span>
                  <span className={`font-black ${item.saldoEfetivo >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                    <MaskedValue permission="view_financial_values" value={item.saldoEfetivo} type="currency" />
                  </span>
                </div>
              </div>
            ))}

            {resumoPorCnpj.length === 0 && (
              <p className="text-xs text-slate-500 text-center py-4">Nenhum lançamento no período filtrado.</p>
            )}
          </div>
        </div>

        {/* Tabela Comparativa: Por Banco de Liquidação */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-xs p-4 space-y-3">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <div className="flex items-center gap-2">
              <Landmark className="w-4 h-4 text-blue-600" />
              <h3 className="text-sm font-bold text-slate-900">Movimentação por Banco (BB, BTG, etc.)</h3>
            </div>
            <span className="text-xs text-slate-500 font-medium">Quanto foi pago/recebido</span>
          </div>

          <div className="space-y-3">
            {resumoPorBanco.map((item) => (
              <div key={item.banco} className="p-3 bg-slate-50/70 border border-slate-200 rounded-xl space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${
                      item.banco.toLowerCase().includes('brasil') 
                        ? 'bg-amber-500' 
                        : item.banco.toLowerCase().includes('btg')
                        ? 'bg-blue-600'
                        : 'bg-slate-600'
                    }`}></span>
                    <span className="text-xs font-bold text-slate-900">{item.banco}</span>
                  </div>
                  <span className="text-[11px] font-semibold text-slate-500 bg-white px-2 py-0.5 rounded border border-slate-200">
                    {item.count} baixas
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 pt-1 text-xs">
                  <div className="p-2 bg-rose-50/70 rounded-lg border border-rose-100">
                    <span className="text-[10px] text-rose-700 uppercase font-bold block">Quanto foi PAGO por este banco:</span>
                    <span className="text-sm font-black text-rose-800">
                      <MaskedValue permission="view_financial_values" value={item.totalPago} type="currency" />
                    </span>
                  </div>
                  <div className="p-2 bg-emerald-50/70 rounded-lg border border-emerald-100">
                    <span className="text-[10px] text-emerald-700 uppercase font-bold block">Quanto foi RECEBIDO neste banco:</span>
                    <span className="text-sm font-black text-emerald-800">
                      <MaskedValue permission="view_financial_values" value={item.totalRecebido} type="currency" />
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-1 border-t border-slate-200/60 text-[11px]">
                  <span className="text-slate-500 font-medium">Fluxo Líquido na Conta:</span>
                  <span className={`font-black ${item.saldoLiquido >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                    <MaskedValue permission="view_financial_values" value={item.saldoLiquido} type="currency" />
                  </span>
                </div>
              </div>
            ))}

            {resumoPorBanco.length === 0 && (
              <p className="text-xs text-slate-500 text-center py-4">Nenhum banco com movimentação no filtro.</p>
            )}
          </div>
        </div>
      </div>

      {/* Painel de Filtros Avançados do Extrato */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-xs p-4 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-3">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-600" />
            <h3 className="text-sm font-bold text-slate-900">Filtros do Extrato</h3>
          </div>

          {/* Atalhos Rápidos de Período */}
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-xs text-slate-500 mr-1 hidden sm:inline">Período:</span>
            {[
              { id: 'mes_atual', label: 'Mês Atual' },
              { id: 'mes_anterior', label: 'Mês Anterior' },
              { id: 'ultimos_3_meses', label: '3 Meses' },
              { id: 'ano_atual', label: 'Ano Atual' },
              { id: 'todos', label: 'Todo Período' }
            ].map(p => (
              <button
                key={p.id}
                type="button"
                onClick={() => handlePeriodChange(p.id)}
                className={`px-2.5 py-1 text-xs font-semibold rounded-lg transition-colors cursor-pointer ${
                  periodPreset === p.id
                    ? 'bg-slate-900 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {/* Filtro CNPJ */}
          <div>
            <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1 flex items-center gap-1">
              <Building2 className="w-3 h-3 text-indigo-600" /> CNPJ / Empresa
            </label>
            <select
              value={selectedCnpj}
              onChange={(e) => setSelectedCnpj(e.target.value)}
              className="w-full text-xs font-semibold rounded-xl border border-slate-300 px-2.5 py-2 bg-slate-50 focus:bg-white focus:border-indigo-500 text-slate-800"
            >
              <option value="all">Todos os CNPJs</option>
              {cnpjsPresentes.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          {/* Filtro Banco */}
          <div>
            <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1 flex items-center gap-1">
              <Landmark className="w-3 h-3 text-blue-600" /> Banco / Liquidação
            </label>
            <select
              value={selectedBanco}
              onChange={(e) => setSelectedBanco(e.target.value)}
              className="w-full text-xs font-semibold rounded-xl border border-slate-300 px-2.5 py-2 bg-slate-50 focus:bg-white focus:border-blue-500 text-slate-800"
            >
              <option value="all">Todos os Bancos</option>
              <option value="Banco do Brasil">Banco do Brasil (BB)</option>
              <option value="BTG">BTG Pactual</option>
              <option value="Bradesco">Bradesco</option>
              <option value="Santander">Santander</option>
              <option value="Itaú">Itaú</option>
              {bancos.map(b => (
                <option key={b.id} value={b.nome}>{b.nome}</option>
              ))}
            </select>
          </div>

          {/* Filtro Tipo */}
          <div>
            <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1">
              Tipo de Lançamento
            </label>
            <select
              value={selectedTipo}
              onChange={(e) => setSelectedTipo(e.target.value as any)}
              className="w-full text-xs font-semibold rounded-xl border border-slate-300 px-2.5 py-2 bg-slate-50 focus:bg-white focus:border-slate-500 text-slate-800"
            >
              <option value="all">Receitas & Despesas</option>
              <option value="pagar">Apenas Contas a Pagar</option>
              <option value="receber">Apenas Contas a Receber</option>
            </select>
          </div>

          {/* Filtro Status */}
          <div>
            <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1">
              Situação / Status
            </label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value as any)}
              className="w-full text-xs font-semibold rounded-xl border border-slate-300 px-2.5 py-2 bg-slate-50 focus:bg-white focus:border-slate-500 text-slate-800"
            >
              <option value="all">Todos os Status</option>
              <option value="quitada">Apenas Quitadas / Pagas</option>
              <option value="aberta">Apenas Em Aberto / Pendentes</option>
            </select>
          </div>

          {/* Data Início */}
          <div>
            <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1 flex items-center gap-1">
              <Calendar className="w-3 h-3 text-slate-400" /> Data Inicial
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setPeriodPreset('custom');
              }}
              className="w-full text-xs font-medium rounded-xl border border-slate-300 px-2.5 py-2 bg-slate-50 focus:bg-white text-slate-800"
            />
          </div>

          {/* Data Fim */}
          <div>
            <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1 flex items-center gap-1">
              <Calendar className="w-3 h-3 text-slate-400" /> Data Final
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setPeriodPreset('custom');
              }}
              className="w-full text-xs font-medium rounded-xl border border-slate-300 px-2.5 py-2 bg-slate-50 focus:bg-white text-slate-800"
            />
          </div>
        </div>

        {/* Linha de Busca Textual */}
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <Search className="h-4 w-4 text-slate-400" />
          </div>
          <input
            type="text"
            placeholder="Filtrar lançamentos por descrição, cliente/fornecedor, NF, ID, banco..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full rounded-xl border border-slate-300 pl-9 pr-4 py-2 text-xs text-slate-800 bg-slate-50 focus:bg-white focus:border-slate-500"
          />
        </div>
      </div>

      {/* Tabela Analítica Detalhada do Extrato */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-bold text-slate-900">Extrato Detalhado de Lançamentos</h3>
            <span className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full font-bold">
              {filteredContas.length} registros
            </span>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs text-slate-600">
            <thead className="bg-slate-50 text-slate-500 font-bold uppercase tracking-wider text-[11px] border-b border-slate-200">
              <tr>
                <th className="px-4 py-3">ID</th>
                <th className="px-4 py-3">Tipo</th>
                <th className="px-4 py-3">Data Ref.</th>
                <th className="px-4 py-3">CNPJ Faturador</th>
                <th className="px-4 py-3">Banco Liquidação</th>
                <th className="px-4 py-3">Descrição / NF</th>
                <th className="px-4 py-3">Favorecido / Cliente</th>
                <th className="px-4 py-3 text-right">Valor Nominal</th>
                <th className="px-4 py-3 text-right">Valor Pago</th>
                <th className="px-4 py-3 text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredContas.map((c) => {
                const isReceita = Number(c.tipo_conta_id) === TipoContaId.RECEBER;
                const isQuitada = Boolean(c.dt_pagamento);
                const cnpj = getNormalizedCnpj(c);
                const banco = getNormalizedBanco(c);

                return (
                  <tr key={c.id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="px-4 py-3 font-bold text-slate-700 whitespace-nowrap">
                      #{c.id}
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full ${
                        isReceita ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                      }`}>
                        {isReceita ? 'Receita' : 'Despesa'}
                      </span>
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap font-medium text-slate-800">
                      <div>
                        {c.dt_pagamento ? (
                          <span className="font-bold text-emerald-700" title="Data do Pagamento">
                            {formatDateBR(c.dt_pagamento)}
                          </span>
                        ) : (
                          <span className="text-slate-600" title="Data do Vencimento">
                            {formatDateBR(c.dt_vencimento)}
                          </span>
                        )}
                        <span className="block text-[10px] text-slate-400">
                          {c.dt_pagamento ? 'Baixado' : 'Vencimento'}
                        </span>
                      </div>
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap font-semibold">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold border bg-indigo-50 text-indigo-700 border-indigo-200">
                        <Building2 className="w-3 h-3" />
                        {cnpj}
                      </span>
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap font-semibold">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold border ${
                        banco.toLowerCase().includes('brasil')
                          ? 'bg-amber-50 text-amber-800 border-amber-200'
                          : banco.toLowerCase().includes('btg')
                          ? 'bg-blue-50 text-blue-800 border-blue-200'
                          : 'bg-slate-50 text-slate-700 border-slate-200'
                      }`}>
                        <Landmark className="w-3 h-3" />
                        {banco}
                      </span>
                    </td>

                    <td className="px-4 py-3 font-medium text-slate-900 max-w-[240px]">
                      <div className="truncate" title={c.descricao || ''}>
                        {sanitizeText(c.descricao) || 'Sem descrição'}
                      </div>
                      {c.numero_nf && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-slate-600 bg-slate-100 px-1 rounded mt-0.5 border border-slate-200">
                          <Receipt className="w-2.5 h-2.5 text-slate-500" />
                          NF: {c.numero_nf}
                        </span>
                      )}
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap font-medium text-slate-700 max-w-[180px] truncate" title={pessoasMap.get(c.pessoa_id) || ''}>
                      {pessoasMap.get(c.pessoa_id) || 'Consumidor / Fornecedor'}
                    </td>

                    <td className="px-4 py-3 text-right font-bold text-slate-900 whitespace-nowrap">
                      <MaskedValue permission="view_financial_values" value={c.valor} type="currency" />
                    </td>

                    <td className="px-4 py-3 text-right font-extrabold whitespace-nowrap">
                      {isQuitada ? (
                        <span className={isReceita ? 'text-emerald-700' : 'text-rose-700'}>
                          <MaskedValue
                            permission="view_financial_values"
                            value={c.valor_pago || c.valor}
                            type="currency"
                          />
                        </span>
                      ) : (
                        <span className="text-slate-400 italic text-[11px]">Pendente</span>
                      )}
                    </td>

                    <td className="px-4 py-3 text-center whitespace-nowrap">
                      {isQuitada ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                          <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                          {isReceita ? 'Recebida' : 'Paga'}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
                          <Clock className="w-3 h-3 text-amber-600" />
                          Aberta
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}

              {filteredContas.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-slate-400 italic">
                    Nenhum lançamento encontrado para os filtros selecionados.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
